import React, { Fragment } from 'react';
import without from 'lodash/without';

const getTitleByPageOnAllWords = (page) =>
  ({
    [SearchGuideModalType.Assessments]: 'with all these words in the title:',
    [SearchGuideModalType.QuestionBank]: 'with all these words:',
    [SearchGuideModalType.StudentAssignments]:
      'with all these words in the assignment name:',
  })[page] || '';

const getTitleByPageOnExactWords = (page) =>
  ({
    [SearchGuideModalType.Assessments]:
      'with this exact word or phrase in the title:',
    [SearchGuideModalType.QuestionBank]: 'with this exact word or phrase:',
    [SearchGuideModalType.StudentAssignments]:
      'with this exact word or phrase  in the assignment name:',
  })[page] || '';

const getTitleByPage = (page, type) =>
  ({
    [SearchTipsType.AllWords]: getTitleByPageOnAllWords,
    [SearchTipsType.ExactWords]: getTitleByPageOnExactWords,
  })[type](page);

export const SearchGuideModalType = {
  Assessments: Symbol('AssessmentsGuideModal'),
  QuestionBank: Symbol('QuestionBankGuideModal'),
  StudentAssignments: Symbol('StudentAssignmentsGuideModal'),
};

export const SearchTipsType = {
  HeaderQuestions: Symbol('HeaderQuestions'),
  HeaderAssessments: Symbol('HeaderAssessments'),
  AllWords: Symbol('AllWords'),
  ExactWords: Symbol('ExactWords'),
  AnyOfWords: Symbol('AnyOfWords'),
  ExcludeWords: Symbol('ExcludeWords'),
  Keywords: Symbol('Keywords'),
  SimilarWords: Symbol('SimilarWords'),
  CombinationOfWords: Symbol('CombinationOfWords'),
};

export const getTableSearchTips = (page = null) => ({
  [SearchTipsType.HeaderQuestions]: {
    title: 'To find questions...',
    description: 'Do this in the search box',
  },
  [SearchTipsType.HeaderAssessments]: {
    title: 'To find assignments...',
    description: 'Do this in the search box',
  },
  [SearchTipsType.AllWords]: {
    title: getTitleByPage(page, SearchTipsType.AllWords),
    description: 'Type the important words: {highlightedText}',
    highlightedText: 'constitution bill',
  },
  [SearchTipsType.ExactWords]: {
    title: getTitleByPage(page, SearchTipsType.ExactWords),
    description: 'Put exact words in quotes: {highlightedText}',
    highlightedText: '"bill of rights"',
  },
  [SearchTipsType.AnyOfWords]: {
    title: 'with any of these words:',
    description: 'Type OR between all the words: {highlightedText}',
    highlightedText: 'constitution OR "bill of rights"',
  },
  [SearchTipsType.ExcludeWords]: {
    title: 'with none of these words:',
    description:
      'Put a minus sign with no space just before each word or phrase to exclude: {highlightedText}',
    highlightedText: 'amendments -"bill of rights"',
  },
  [SearchTipsType.Keywords]: {
    title: 'by keyword',
    description:
      'Use quotes: {highlightedText} You can also filter by keyword while searching.',
    highlightedText: '"Skill 1.A"',
  },
  [SearchTipsType.SimilarWords]: {
    title: 'with words that are similar',
    description:
      'Use an asterisk to find words with the same stem: {highlightedText} will find {highlightedText} and {highlightedText}',
    highlightedText: ['constitution*', 'constitution', 'constitutional'],
  },
  [SearchTipsType.CombinationOfWords]: {
    title: 'combining any of the above',
    description:
      'Use parentheses to group phrases: {highlightedText} will find {highlightedText} or {highlightedText}, and also {highlightedText}',
    highlightedText: [
      '(Jefferson OR Hamilton) constitution',
      'Jefferson',
      'Hamilton',
      'constitution',
    ],
  },
});

export const SearchTipsByPageType = {
  [SearchGuideModalType.Assessments]: [
    SearchTipsType.HeaderAssessments,
    SearchTipsType.AllWords,
    SearchTipsType.ExactWords,
  ],
  [SearchGuideModalType.QuestionBank]: without(
    Reflect.ownKeys(getTableSearchTips()),
    SearchTipsType.HeaderAssessments,
  ),
  [SearchGuideModalType.StudentAssignments]: [
    SearchTipsType.HeaderAssessments,
    SearchTipsType.AllWords,
    SearchTipsType.ExactWords,
  ],
};

export const SearchModalTitleByPageType = {
  [SearchGuideModalType.Assessments]: 'Resources Title Search',
  [SearchGuideModalType.QuestionBank]: 'Question Bank Search',
  [SearchGuideModalType.StudentAssignments]: 'Assignments Search',
};

export const SearchModalContentByPageType = {
  [SearchGuideModalType.Assessments]: () => (
    <Fragment>
      <p className="m-0 text-14 leading-8">
        Type the search terms and press Enter to start your search.
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        <span>
          To clear your search results, click the "x" in the search box or
          delete your text and press Enter.
        </span>
      </p>
    </Fragment>
  ),
  [SearchGuideModalType.QuestionBank]: () => (
    <Fragment>
      <p className="m-0 text-14 leading-8">
        Search questions by entering any words or phrases included in the
        prompt, stimulus, or answer choices.
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        <span>
          You can also search questions by keywords. Enter{' '}
          <code>&quot;Skill 1.A&quot;</code> to find all questions that assess
          Skill 1.A.
        </span>
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        <span>Press the Enter/Return key to start your search.</span>
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        You can use search together with filters to narrow down results, for
        example to specify keyword tags you want to exclude.
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        <span>
          To clear your search results, click the &quot;x&quot; in the search
          box or delete your text and press Enter/Return.
        </span>
      </p>
    </Fragment>
  ),
  [SearchGuideModalType.StudentAssignments]: () => (
    <Fragment>
      <p className="m-0 text-14 leading-8">
        Type the search terms and press Enter to start your search.
      </p>
      <p className="m-0 text-14 mt-4.2 leading-8">
        <span>
          To clear your search results, click the "x" in the search box or
          delete your text and press Enter.
        </span>
      </p>
    </Fragment>
  ),
};
